export default () => {
  const ANNUAL = 'Annual';
  // Access type is no longer a filter, so its parameter is only listed here to
  // be cleared out of a url that was bookmarked while it still was one.
  const FILTER_PARAMS = ['category', 'payment_type', 'coverage'];
  // Each filter reads its own values off a card and is constrained by the other.
  const FILTERS = [
    { key: 'category', other: 'paymentType', param: 'category' },
    { key: 'paymentType', other: 'category', param: 'payment_type' },
  ];

  function getSelects(el) {
    return {
      category: el.querySelector('[data-membership-filters-target="category"]'),
      paymentType: el.querySelector('[data-membership-filters-target="paymentType"]'),
      empty: el.querySelector('[data-membership-filters-target="empty"]'),
    };
  }

  // Only the cards in the rendered groups are filtered — the curated Most
  // Popular section keeps its own copies of them untouched.
  function allCards(el) {
    if (!el._membershipCards) {
      el._membershipCards = [...el.querySelectorAll('.membership-groups .membership-type')];
    }
    return el._membershipCards;
  }

  function popularSection(el) {
    return el.querySelector('.membership-group--popular');
  }

  function filtersActive(values) {
    return Object.values(values).some(Boolean);
  }

  function selectedValues(el) {
    const { category, paymentType } = getSelects(el);
    return {
      category: category ? category.value : '',
      paymentType: paymentType ? paymentType.value : '',
    };
  }

  function matchesMulti(value, data) {
    if (!value) return true;
    return (data || '').split('|').filter(Boolean).includes(value);
  }

  function cardMatches(card, values) {
    return (
      matchesMulti(values.category, card.dataset.category) &&
      matchesMulti(values.paymentType, card.dataset.paymentType)
    );
  }

  function updateSelectState(sel) {
    if (!sel) return;
    sel.classList.toggle('membership-filters__select--active', sel.value !== '');
  }

  // The full option set the server rendered, kept so a filter can be widened
  // back out again once the other one stops constraining it.
  function optionSnapshot(el) {
    if (!el._filterOptions) {
      el._filterOptions = {};
      FILTERS.forEach(({ key }) => {
        const sel = getSelects(el)[key];
        const options = sel ? [...sel.options] : [];
        el._filterOptions[key] = {
          placeholder: options.length ? options[0].textContent : '',
          values: options.slice(1).map(option => option.value),
        };
      });
    }
    return el._filterOptions;
  }

  function cardValuesFor(card, key) {
    return (card.dataset[key] || '').split('|').filter(Boolean);
  }

  // The options a filter can offer are the ones still backed by a membership
  // once the other filter's selection has been applied, so a visitor can never
  // build a combination that matches nothing.
  function availableFor(el, key, otherKey, otherValue) {
    const available = new Set();
    allCards(el).forEach(card => {
      if (!matchesMulti(otherValue, card.dataset[otherKey])) return;
      cardValuesFor(card, key).forEach(value => available.add(value));
    });
    return available;
  }

  function renderOptions(el, key, available) {
    const sel = getSelects(el)[key];
    if (!sel) return;
    const { placeholder, values } = optionSnapshot(el)[key];
    const current = sel.value;
    sel.textContent = '';
    sel.appendChild(new Option(placeholder, ''));
    values.filter(value => available.has(value)).forEach(value =>
      sel.appendChild(new Option(value, value)));
    sel.value = [...sel.options].some(option => option.value === current) ? current : '';
    updateSelectState(sel);
  }

  function clearIfUnavailable(el, key, available) {
    const sel = getSelects(el)[key];
    if (!sel || !sel.value || available.has(sel.value)) return;
    sel.value = '';
  }

  // Narrowing one filter can invalidate the other's selection, and clearing
  // that selection widens the first again, so both are settled before either
  // option list is rendered.
  function constrainFilters(el) {
    FILTERS.forEach(({ key, other }) => {
      const values = selectedValues(el);
      clearIfUnavailable(el, key, availableFor(el, key, other, values[other]));
    });
    FILTERS.forEach(({ key, other }) => {
      const values = selectedValues(el);
      renderOptions(el, key, availableFor(el, key, other, values[other]));
    });
    return selectedValues(el);
  }

  // Most Popular is a shortcut through the unfiltered list, so it steps aside
  // as soon as a filter narrows the page and returns when the filters clear.
  function updatePopularSection(el, hidden) {
    const section = popularSection(el);
    if (!section) return;
    section.style.display = hidden ? 'none' : '';
    if (hidden) section.open = false;
  }

  function groupContainers(el) {
    return [...el.querySelectorAll('.membership-groups')];
  }

  function activeContainer(el) {
    return groupContainers(el).find(container => !container.hidden);
  }

  // The list is grouped by whichever taxonomy the visitor is not filtering on:
  // who it's for by default, and membership type once the who it's for filter
  // has narrowed the page to a single audience.
  function groupingMode(values) {
    return values.category ? 'type' : 'eligibility';
  }

  // Headings are matched on their label rather than through a selector: the
  // labels come from central and can carry any character.
  function groupFor(container, label) {
    return [...container.querySelectorAll('.membership-group')]
      .find(group => group.dataset.group === label);
  }

  // Cards move between the two groupings rather than being rendered under both,
  // so a card carries its plan toggle and its tracking payload across with it.
  // Server order is kept: the cards are walked in the order they were rendered.
  function applyGrouping(el, mode) {
    const active = groupContainers(el).find(c => c.dataset.grouping === mode);
    if (!active) return;
    const key = mode === 'type' ? 'typeGroup' : 'eligibilityGroup';
    allCards(el).forEach(card => {
      const target = groupFor(active, card.dataset[key])?.querySelector('.membership-types');
      if (target && card.parentElement !== target) target.appendChild(card);
    });
    groupContainers(el).forEach(container => { container.hidden = container !== active; });
  }

  // A group whose memberships are all filtered out is not shown at all, its
  // heading included.
  function updateSectionVisibility(el) {
    const active = activeContainer(el);
    if (!active) return;
    active.querySelectorAll('.membership-group').forEach(group => {
      const visible = [...group.querySelectorAll('.membership-type')]
        .filter(card => card.style.display !== 'none');
      group.style.display = visible.length ? '' : 'none';
      const count = group.querySelector('.membership-group__count');
      if (count) count.textContent = `${visible.length} membership${visible.length === 1 ? '' : 's'}`;
    });
  }

  // Most Popular sits outside both groupings, so it heads the list whichever
  // one is on show.
  function visibleGroups(el) {
    const active = activeContainer(el);
    return [popularSection(el), ...(active ? active.querySelectorAll('.membership-group') : [])]
      .filter(group => group && group.style.display !== 'none');
  }

  function autoOpened(el) {
    if (!el._autoOpened) el._autoOpened = new WeakSet();
    return el._autoOpened;
  }

  // BOU-2469: single-open accordion — only the first visible group starts open.
  // Auto-opened groups are tracked so they are not scrolled into view. A group
  // the filters have hidden is closed as well, or it would come back open when
  // the filters widen again.
  function openFirstGroup(el) {
    const [first] = visibleGroups(el);
    el.querySelectorAll('.membership-group').forEach(group => {
      if (group === first && !group.open) autoOpened(el).add(group);
      group.open = group === first;
    });
  }

  function collapseOthers(el, opened) {
    el.querySelectorAll('.membership-group')
      .forEach(group => { if (group !== opened) group.open = false; });
  }

  // On a user expand, collapse the rest and bring the opened group to the top —
  // collapsing the group above can otherwise leave its header off-screen.
  function onGroupToggle(el, opened) {
    if (!opened.matches?.('.membership-group') || !opened.open) return;
    collapseOthers(el, opened);
    if (autoOpened(el).delete(opened)) return;
    opened.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  // Annual is a view as well as a filter: a card it matches opens on its annual
  // plan, and every other selection leaves the card on its monthly one.
  function applyPlanView(el, values) {
    allCards(el).forEach(card => {
      const toggle = card.querySelector('.membership-type__toggle');
      if (toggle) toggle.checked = values.paymentType === ANNUAL && card.style.display !== 'none';
    });
  }

  function applyFilters(el) {
    const values = constrainFilters(el);
    let anyVisible = false;

    allCards(el).forEach(card => {
      const match = cardMatches(card, values);
      card.style.display = match ? '' : 'none';
      if (match) anyVisible = true;
    });

    applyPlanView(el, values);
    applyGrouping(el, groupingMode(values));
    updateSectionVisibility(el);
    updatePopularSection(el, filtersActive(values));
    openFirstGroup(el);

    const { empty } = getSelects(el);
    if (empty) empty.hidden = anyVisible;
  }

  function collectFilterParams(el) {
    const values = selectedValues(el);
    const params = {};
    FILTERS.forEach(({ key, param }) => {
      if (values[key]) params[param] = values[key];
    });
    return params;
  }

  function syncUrl(el) {
    const url = new URL(window.location.href);
    FILTER_PARAMS.forEach(k => url.searchParams.delete(k));
    Object.entries(collectFilterParams(el)).forEach(([k, v]) => url.searchParams.set(k, v));
    history.replaceState(null, '', url.toString());
  }

  function setValue(sel, value) {
    if (!sel || !value) return;
    if (![...sel.options].some(o => o.value === value)) return;
    sel.value = value;
    updateSelectState(sel);
  }

  function restoreFromUrl(el) {
    const { category, paymentType } = getSelects(el);
    const p = new URLSearchParams(window.location.search);
    setValue(category, p.get('category'));
    setValue(paymentType, p.get('payment_type'));
    applyFilters(el);
  }

  function resetFilters(el) {
    const { category, paymentType } = getSelects(el);
    [category, paymentType].forEach(sel => {
      if (sel) sel.value = '';
      updateSelectState(sel);
    });
    applyFilters(el);
    syncUrl(el);
  }

  function initFilters(el) {
    if (el._membershipFiltersAbort) el._membershipFiltersAbort.abort();
    const ctrl = new AbortController();
    el._membershipFiltersAbort = ctrl;
    const { signal } = ctrl;

    allCards(el);
    optionSnapshot(el);
    restoreFromUrl(el);

    // toggle does not bubble, so listen in the capture phase on the root — the
    // Most Popular section sits outside the rendered groups container.
    el.addEventListener(
      'toggle', e => onGroupToggle(el, e.target), { capture: true, signal }
    );

    FILTERS.forEach(({ key }) => {
      el.querySelector(`[data-membership-filters-target="${key}"]`)
        ?.addEventListener('change', e => {
          updateSelectState(e.target);
          applyFilters(el);
          syncUrl(el);
        }, { signal });
    });

    el.querySelectorAll('[data-membership-filters-target="reset"]').forEach(btn =>
      btn.addEventListener('click', e => {
        e.preventDefault();
        resetFilters(el);
      }, { signal }));
  }

  function initAll() {
    document.querySelectorAll('[data-controller="membership-filters"]').forEach(initFilters);
  }

  document.addEventListener('DOMContentLoaded', initAll);
};
